import { useState, useEffect, useCallback, useMemo } from "react";
import { api, useAuth } from "@/lib/auth";
import { Icon } from "@/components/kit";
import { toast } from "sonner";

const PRIMARY = [
  { key: "all", label: "All" },
  { key: "near", label: "Near me" },
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "online", label: "Online" },
  { key: "offline", label: "Offline" },
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtLong = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  const mi = parseInt(m, 10) - 1;
  return `${parseInt(d, 10)} ${MONTHS[mi] || ""} ${y}`;
};
const dayNum = (iso) => (iso ? String(parseInt(iso.split("-")[2], 10)).padStart(2, "0") : "--");
const monShort = (iso) => (iso ? (MONTHS[parseInt(iso.split("-")[1], 10) - 1] || "").toUpperCase() : "");

const copyText = (text) => {
  try {
    const p = navigator.clipboard?.writeText(text);
    if (p && p.catch) p.catch(() => {});
  } catch (e) { console.error("Clipboard unavailable", e); }
};

const track = (event, ev = {}) =>
  api.post("/community/iap/track", {
    event, event_id: ev.id || "", city: ev.city || "", topic: ev.topic || "", format: ev.format || "",
  }).catch(() => {});

const buildShareMessage = (ev) => {
  const lines = [
    "*Investor Awareness Programme*",
    "",
    `${ev.amc_name || "An AMC"} is conducting an AMFI Investor Awareness Programme${ev.city ? ` in ${ev.city}` : ""}.`,
    "",
    `🗓 Date: ${fmtLong(ev.date)}`,
    ev.time ? `⏰ Time: ${ev.time}` : "",
    ev.format === "online" ? "💻 Format: Online / Webinar" : (ev.address ? `📍 Venue: ${ev.address}` : ""),
    ev.contact_person ? `👤 Contact: ${ev.contact_person}${ev.contact_details ? ` (${ev.contact_details})` : ""}` : "",
    "",
    "Source: AMFI",
    ev.source_url || "https://www.amfiindia.com/investor/awareness-program",
    "",
    "Shared via MFDx",
  ];
  return lines.filter(Boolean).join("\n");
};

function FormatPill({ format }) {
  const online = format === "online";
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 ${online ? "bg-blue-50 text-blue-600" : "bg-amber-50 text-amber-700"}`}>
      <Icon name={online ? "Video" : "MapPin"} size={11} /> {online ? "ONLINE" : "OFFLINE"}
    </span>
  );
}

function ShareMenu({ ev, onDone }) {
  const msg = buildShareMessage(ev);
  const whatsapp = () => { track("investor_awareness_whatsapp_share", ev); window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank"); onDone(); };
  const copy = () => { copyText(msg); track("investor_awareness_copy", ev); toast.success("Programme details are ready to send."); onDone(); };
  const native = async () => {
    track("investor_awareness_share", ev);
    if (navigator.share) { try { await navigator.share({ title: "Investor Awareness Programme", text: msg }); } catch (e) { /* cancelled */ } }
    else { copyText(msg); toast.success("Copied — ready to share."); }
    onDone();
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid="iap-share-menu">
      <button onClick={whatsapp} data-testid="iap-share-whatsapp" className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-500 text-white text-sm font-semibold px-3.5 py-2 hover:brightness-105">
        <Icon name="MessageCircle" size={15} /> WhatsApp
      </button>
      <button onClick={copy} data-testid="iap-share-copy" className="inline-flex items-center gap-1.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-semibold px-3.5 py-2 hover:border-gray-300">
        <Icon name="Copy" size={15} /> Copy
      </button>
      <button onClick={native} data-testid="iap-share-native" className="inline-flex items-center gap-1.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-semibold px-3.5 py-2 hover:border-gray-300">
        <Icon name="Share2" size={15} /> Share
      </button>
    </div>
  );
}

function EventCard({ ev, onOpen }) {
  const [share, setShare] = useState(false);
  return (
    <div data-testid="iap-event-card" className="card-mfx p-0 overflow-hidden hover-lift flex flex-col">
      <div className="flex">
        <div className="w-[84px] shrink-0 bg-[#0A0A0A] text-white flex flex-col items-center justify-center py-5">
          <span className="font-head font-bold text-3xl leading-none text-amber-400">{dayNum(ev.date)}</span>
          <span className="text-[11px] font-semibold tracking-widest text-gray-300 mt-1">{monShort(ev.date)}</span>
          <span className="text-[10px] text-gray-500 mt-0.5">{ev.day}</span>
        </div>
        <div className="flex-1 min-w-0 p-4">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            {ev.date_label && <span className="text-[11px] font-bold text-amber-600 bg-amber-50 rounded-full px-2 py-0.5">{ev.date_label}</span>}
            {ev.is_new && <span className="text-[11px] font-bold text-emerald-600 bg-emerald-50 rounded-full px-2 py-0.5" data-testid="iap-new-badge">NEW</span>}
          </div>
          <div className="flex items-center gap-1 text-gray-500 text-xs mb-1"><Icon name="MapPin" size={12} /> {ev.city || "—"}{ev.time ? ` · ${ev.time}` : ""}</div>
          <h3 className="font-head font-semibold text-[15px] text-gray-900 leading-snug truncate">{ev.amc_name || ev.title}</h3>
          {ev.topic && <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{ev.topic}</p>}
          <div className="flex items-center gap-2 mt-2.5">
            <span className="text-[10px] font-bold text-gray-400 tracking-wide">AMFI</span>
            <span className="text-gray-300">·</span>
            <FormatPill format={ev.format} />
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2.5 bg-gray-50/60">
        <button onClick={() => { onOpen(ev); }} data-testid="iap-view-details" className="text-sm font-semibold text-gray-700 inline-flex items-center gap-1 hover:text-amber-600">
          View details <Icon name="ArrowRight" size={14} />
        </button>
        {share ? <ShareMenu ev={ev} onDone={() => setShare(false)} /> : (
          <button onClick={() => { setShare(true); track("investor_awareness_share", ev); }} data-testid="iap-share-btn" className="text-sm font-semibold text-amber-600 inline-flex items-center gap-1 hover:text-amber-700">
            Share <Icon name="Share2" size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function DetailModal({ ev, onClose }) {
  const [share, setShare] = useState(false);
  useEffect(() => { track("investor_awareness_event_view", ev); }, [ev]);
  const addCalendar = () => {
    track("investor_awareness_calendar_add", ev);
    const dt = (ev.date || "").replace(/-/g, "");
    const title = encodeURIComponent(`AMFI Investor Awareness Programme — ${ev.amc_name || ""}`);
    const details = encodeURIComponent(buildShareMessage(ev));
    const loc = encodeURIComponent(ev.format === "online" ? "Online / Webinar" : (ev.address || ev.city || ""));
    if (dt) window.open(`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dt}/${dt}&details=${details}&location=${loc}`, "_blank");
    toast.success("Added to calendar.");
  };
  const Field = ({ label, value }) => (
    <div><div className="text-[11px] uppercase tracking-widest text-gray-400 font-semibold">{label}</div>
      <div className="text-sm text-gray-800 mt-0.5">{value || <span className="text-gray-400">Not provided by AMFI.</span>}</div></div>
  );
  return (
    <div className="fixed inset-0 z-[90] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="iap-detail-modal">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#0A0A0A] text-white p-5 sm:rounded-t-2xl relative">
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white" data-testid="iap-detail-close"><Icon name="X" size={20} /></button>
          <div className="flex items-center gap-2 mb-2">
            {ev.date_label && <span className="text-[11px] font-bold text-black bg-amber-400 rounded-full px-2 py-0.5">{ev.date_label}</span>}
            <FormatPill format={ev.format} />
          </div>
          <h2 className="font-head font-bold text-xl leading-snug">Investor Awareness Programme</h2>
          <p className="text-gray-300 text-sm mt-1">{ev.amc_name}</p>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <Field label="Date" value={fmtLong(ev.date)} />
          <Field label="Day" value={ev.day} />
          <Field label="Time" value={ev.time} />
          <Field label="City" value={ev.city} />
          <div className="col-span-2"><Field label={ev.format === "online" ? "Platform" : "Venue / Address"} value={ev.format === "online" ? "Online / Webinar" : ev.address} /></div>
          <div className="col-span-2"><Field label="Topic / Audience" value={ev.topic} /></div>
          <Field label="Contact person" value={ev.contact_person} />
          <Field label="Contact" value={ev.contact_details} />
        </div>
        <div className="px-5 pb-4 -mt-1 text-[11px] text-gray-400 flex items-center gap-2">
          <Icon name="ShieldCheck" size={13} /> Source: AMFI · Last updated {ev.last_synced_at ? new Date(ev.last_synced_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 space-y-3">
          {share ? <ShareMenu ev={ev} onDone={() => setShare(false)} /> : (
            <button onClick={() => setShare(true)} data-testid="iap-detail-share" className="w-full grad-amber text-black font-semibold rounded-xl py-3 inline-flex items-center justify-center gap-2 amber-glow">
              <Icon name="Share2" size={16} /> Share with investor
            </button>
          )}
          <div className="flex gap-2">
            <button onClick={addCalendar} data-testid="iap-detail-calendar" className="flex-1 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl py-2.5 inline-flex items-center justify-center gap-2 hover:border-gray-300">
              <Icon name="CalendarPlus" size={15} /> Add to calendar
            </button>
            <a href={ev.source_url} target="_blank" rel="noreferrer" onClick={() => track("investor_awareness_registration", ev)} className="flex-1 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl py-2.5 inline-flex items-center justify-center gap-2 hover:border-gray-300" data-testid="iap-detail-source">
              <Icon name="ExternalLink" size={15} /> View on AMFI
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function InvestorAwareness() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [city, setCity] = useState("");
  const [more, setMore] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => { track("investor_awareness_page_view"); api.get("/community/iap/summary").then((r) => setSummary(r.data)).catch(() => {}); }, []);

  const loadEvents = useCallback(() => {
    setError(false);
    api.get("/community/iap/events", { params: { q, filter, city } })
      .then((r) => setEvents(r.data))
      .catch(() => { setError(true); setEvents([]); });
  }, [q, filter, city]);

  useEffect(() => {
    const t = setTimeout(loadEvents, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadEvents, q]);

  const upNext = useMemo(() => (events && events.length ? events[0] : null), [events]);
  const rest = useMemo(() => (events ? events.slice(upNext ? 1 : 0) : []), [events, upNext]);
  const lastUpdated = summary?.last_synced_at ? new Date(summary.last_synced_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : null;

  const openDetail = (ev) => { setDetail(ev); };
  const setF = (f) => { setFilter(f); track("investor_awareness_filter", { topic: f }); if (f === "near") track("investor_awareness_near_me"); };

  return (
    <div className="fade-up" data-testid="investor-awareness-page">
      {/* Hero */}
      <div className="rounded-[24px] bg-[#0A0A0A] text-white p-6 md:p-8 mb-6 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full grad-amber opacity-20 blur-3xl" />
        <div className="relative">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-400 mb-3">Community / Investor Awareness</div>
          <h1 className="font-head font-bold text-3xl md:text-4xl tracking-tight max-w-2xl">Help investors stay informed.</h1>
          <p className="text-gray-300 text-sm md:text-base mt-3 max-w-xl measure">Discover upcoming Investor Awareness Programmes from AMFI — and share what's relevant with your investors.</p>
          <div className="flex flex-wrap items-center gap-3 mt-6">
            <a href="#iap-programmes" className="grad-amber text-black font-semibold rounded-xl px-5 py-2.5 inline-flex items-center gap-2 amber-glow" data-testid="iap-explore-btn"><Icon name="Compass" size={16} /> Explore programmes</a>
            <span className="text-xs text-gray-400 inline-flex items-center gap-1"><Icon name="ShieldCheck" size={13} /> Source: AMFI{lastUpdated ? ` · Updated ${lastUpdated}` : ""}</span>
          </div>
        </div>
      </div>

      {/* TODAY ON MFDx */}
      {summary && (
        <div className="card-mfx p-4 mb-6 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="iap-today-strip">
          <div className="font-head font-bold text-sm uppercase tracking-wide text-gray-900 flex items-center gap-2"><Icon name="Sparkles" size={16} className="text-amber-500" /> Today on MFDx</div>
          {summary.new > 0 && <span className="text-sm text-gray-600"><b className="text-emerald-600">{summary.new}</b> new programme{summary.new > 1 ? "s" : ""}</span>}
          {summary.today > 0 && <span className="text-sm text-gray-600"><b className="text-amber-600">{summary.today}</b> happening today</span>}
          {summary.city && summary.near > 0 && <span className="text-sm text-gray-600"><b className="text-gray-900">{summary.near}</b> near {summary.city}</span>}
          <span className="text-sm text-gray-500"><b className="text-gray-900">{summary.total}</b> upcoming across India</span>
          {lastUpdated && <span className="text-xs text-gray-400 ml-auto">Updated {lastUpdated}</span>}
        </div>
      )}

      {/* Search + filters */}
      <div className="mb-5">
        <div className="relative mb-3">
          <Icon name="Search" size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); if (e.target.value) track("investor_awareness_search", { topic: e.target.value }); }} data-testid="iap-search"
            placeholder="Search city, topic or programme..." className="w-full rounded-xl border border-gray-200 bg-white pl-11 pr-4 py-3 text-sm outline-none focus:border-amber-400" />
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {PRIMARY.map((f) => (
            <button key={f.key} onClick={() => setF(f.key)} data-testid={`iap-filter-${f.key}`}
              className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === f.key ? "bg-[#0A0A0A] text-white border-transparent" : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"}`}>
              {f.label}
            </button>
          ))}
          <button onClick={() => setMore((m) => !m)} data-testid="iap-more-filters" className="whitespace-nowrap px-3.5 py-1.5 rounded-full text-sm font-medium border bg-white border-gray-200 text-gray-600 hover:border-gray-300 inline-flex items-center gap-1">
            <Icon name="SlidersHorizontal" size={14} /> More
          </button>
        </div>
        {more && summary && (
          <div className="mt-3 flex flex-wrap gap-3 items-center">
            <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="iap-city-select" className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-amber-400">
              <option value="">All cities</option>
              {summary.cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {(city || q || filter !== "all") && (
              <button onClick={() => { setCity(""); setQ(""); setFilter("all"); }} data-testid="iap-clear-filters" className="text-sm text-gray-500 hover:text-gray-800 inline-flex items-center gap-1"><Icon name="X" size={14} /> Clear</button>
            )}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="card-mfx p-6 text-center mb-6" data-testid="iap-error">
          <Icon name="CloudOff" size={28} className="text-gray-300 mx-auto mb-2" />
          <p className="font-semibold text-gray-800">AMFI information is temporarily unavailable.</p>
          <p className="text-sm text-gray-500 mt-1">Showing the latest successfully synchronized programmes when available.{lastUpdated ? ` Last updated: ${lastUpdated}.` : ""}</p>
        </div>
      )}

      {/* UP NEXT */}
      {upNext && (
        <div className="mb-7" data-testid="iap-up-next">
          <div className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-2">Up next</div>
          <div className="card-mfx p-0 overflow-hidden flex flex-col sm:flex-row">
            <div className="sm:w-40 bg-[#0A0A0A] text-white flex sm:flex-col items-center justify-center gap-2 sm:gap-0 py-5">
              <span className="font-head font-bold text-4xl text-amber-400 leading-none">{dayNum(upNext.date)}</span>
              <div className="flex flex-col items-center"><span className="text-xs font-semibold tracking-widest text-gray-300">{monShort(upNext.date)}</span><span className="text-[11px] text-gray-500">{upNext.day}</span></div>
            </div>
            <div className="flex-1 p-5">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                {upNext.date_label && <span className="text-[11px] font-bold text-amber-600 bg-amber-50 rounded-full px-2 py-0.5">{upNext.date_label}</span>}
                <span className="text-[10px] font-bold text-gray-400 tracking-wide">AMFI</span><FormatPill format={upNext.format} />
              </div>
              <div className="flex items-center gap-1 text-gray-500 text-xs mb-1"><Icon name="MapPin" size={12} /> {upNext.city}{upNext.time ? ` · ${upNext.time}` : ""}</div>
              <h3 className="font-head font-bold text-lg text-gray-900">{upNext.amc_name || upNext.title}</h3>
              {upNext.topic && <p className="text-sm text-gray-500 mt-0.5">{upNext.topic}</p>}
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <button onClick={() => openDetail(upNext)} data-testid="iap-upnext-details" className="grad-amber text-black text-sm font-semibold rounded-xl px-4 py-2 inline-flex items-center gap-1.5"><Icon name="ArrowRight" size={15} /> View details</button>
                <button onClick={() => { copyText(buildShareMessage(upNext)); track("investor_awareness_copy", upNext); toast.success("Programme details are ready to send."); }} className="bg-white border border-gray-200 text-gray-700 text-sm font-semibold rounded-xl px-4 py-2 inline-flex items-center gap-1.5 hover:border-gray-300"><Icon name="Share2" size={15} /> Share</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upcoming */}
      <div id="iap-programmes">
        {summary?.city && summary?.near > 0 && (
          <div className="text-sm text-gray-600 mb-3" data-testid="iap-near-you">Investor Awareness Programmes near <b className="text-gray-900">{summary.city}</b> · {summary.near} upcoming</div>
        )}
        {!summary?.city && (
          <div className="text-sm text-gray-500 mb-3">Select your city under <b>More</b> filters to see nearby programmes.</div>
        )}
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-head font-bold text-xl text-gray-900">Upcoming programmes</h2>
          {events && <span className="text-sm text-gray-400">{events.length} found</span>}
        </div>

        {events === null ? (
          <div className="grid sm:grid-cols-2 gap-4">
            {[0, 1, 2, 3].map((i) => <div key={i} className="card-mfx h-32 animate-pulse bg-gray-100" />)}
          </div>
        ) : rest.length === 0 && !upNext ? (
          <div className="card-mfx p-10 text-center" data-testid="iap-empty">
            <Icon name="CalendarX" size={30} className="text-gray-300 mx-auto mb-3" />
            <p className="font-semibold text-gray-800">No programmes found</p>
            <p className="text-sm text-gray-500 mt-1">Try another city, date or filter.</p>
            <button onClick={() => { setCity(""); setQ(""); setFilter("all"); }} className="mt-4 text-sm font-semibold text-amber-600" data-testid="iap-empty-clear">Clear filters</button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {rest.map((ev) => <EventCard key={ev.id} ev={ev} onOpen={openDetail} />)}
          </div>
        )}
      </div>

      {/* Community signal + disclaimer */}
      <div className="mt-8 card-mfx p-5" data-testid="iap-community-signal">
        <div className="font-head font-bold text-sm uppercase tracking-wide text-gray-900 flex items-center gap-2 mb-1"><Icon name="Users" size={16} className="text-amber-500" /> What's happening in investor education</div>
        <p className="text-sm text-gray-500">Stay connected with investor education initiatives happening across India, sourced directly from AMFI.</p>
      </div>
      <p className="text-[11px] text-gray-400 mt-4 flex items-start gap-2">
        <Icon name="Info" size={13} className="mt-0.5 shrink-0" />
        Programme information is sourced from AMFI. Please verify details on the official <a href="https://www.amfiindia.com/investor/awareness-program" target="_blank" rel="noreferrer" className="underline">AMFI website</a> before attending. MFDx is not affiliated with AMFI.
      </p>

      {detail && <DetailModal ev={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
